/**
 * SiFa-Kundenbereich („Kundenportal") — gemeinsame Typen.
 *
 * Zwei Seiten, zwei Sichten:
 *  • INNENDIENST: `SifaKundenzugang` = eine Zeile aus `sifa_kundenzugaenge`
 *    (supabase/sifa_kundenportal.sql, MIGRATIONS.md Nr. 93) — OHNE `pin_hash`,
 *    den bekommt der Client nie (Spaltenrechte).
 *  • KUNDE: `KundenportalDaten` = das jsonb, das `sifa_kundenportal_laden`
 *    (Nr. 94) in EINEM Aufruf liefert. Es enthaelt bewusst NICHT: Unterschriften,
 *    Antworten, Teilnahme-Tokens, Storage-Pfade, Preise, Begehungsberichte.
 *
 * Der Mapper jsonb → Typen steht in kundenportalLogic.ts (rein, getestet).
 */

/** Sechsstellige PIN; Vergleich serverseitig ueber sha256(pin||token). */
export const PIN_LAENGE = 6;

/** Ab so vielen Fehlversuchen sperrt die Datenbank fuer eine Stunde (Nr. 94). */
export const MAX_FEHLVERSUCHE = 10;

/** Ein Kundenzugang je Projekt — die Innendienst-Sicht (ohne pin_hash). */
export interface SifaKundenzugang {
  id: string;
  projectId: string;
  /** 64 Hex aus zwei UUIDs (Spaltendefault). Steht im Link #/kundenportal/<token>. */
  token: string;
  aktiv: boolean;
  /** ISO-Zeitstempel oder null = ohne Ablauf. */
  aktivBis: string | null;
  fehlversuche: number;
  /** Gesetzt = gesperrt bis dahin (Fehlversuche). */
  gesperrtBis: string | null;
  /** Letzte erfolgreiche Anmeldung des Kunden. */
  letzterZugriff: string | null;
  companyName: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Betriebsdaten, wie das Portal sie zeigt (aus dem Projekt-JSON, nur Anzeige). */
export interface PortalBetrieb {
  name: string;
  projektnummer: string | null;
  adresse: string | null;
  kundeName: string | null;
  branche: string | null;
  berufsgenossenschaft: string | null;
  beschaeftigte: number | null;
  betreuungsgruppe: string | null;
  betriebsarzt: string | null;
  sicherheitsbeauftragte: string | null;
  ersthelfer: number | null;
}

/** Ansprechpartner der SiFa — nur Name, Telefon, E-Mail. */
export interface PortalKontakt {
  name: string | null;
  telefon: string | null;
  email: string | null;
}

export interface PortalMitarbeiter {
  id: string;
  vorname: string;
  nachname: string;
  /** ISO YYYY-MM-DD. */
  geburtsdatum: string | null;
  email: string | null;
  abteilung: string | null;
  personalnummer: string | null;
  /** false = ausgeschieden/deaktiviert; die Zeile bleibt (Teilnahmen haengen daran). */
  aktiv: boolean;
  updatedAt: string;
}

export interface PortalUnterweisung {
  id: string;
  nummer: number;
  titel: string;
  beschreibung: string | null;
  /** 'aktiv' | 'archiviert' — Entwuerfe kommen nicht ins Portal. */
  status: string;
  /** Gueltigkeit des Nachweises in Monaten; 0 = ohne Ablauf. */
  gueltigMonate: number;
  dauerMin: number | null;
  /** Nur gesetzt, wenn der Kundenlink gerade aktiv ist — zum Weiterleiten. */
  linkToken: string | null;
}

export interface PortalTeilnahme {
  id: string;
  mitarbeiterId: string | null;
  vorname: string | null;
  nachname: string | null;
  unterweisungId: string;
  abgeschlossenAm: string | null;
  bestanden: boolean;
  /** ISO YYYY-MM-DD; null = ohne Ablauf oder nicht bestanden. */
  gueltigBis: string | null;
  zertifikatNr: string | null;
  /** true = das PDF liegt im Bucket und laesst sich ueber den Endpunkt holen. */
  zertifikatVorhanden: boolean;
}

/** Offene Frist aus `sifa_fristen` (Nr. 92); die Liste bleibt leer, solange die Tabelle fehlt. */
export interface PortalFrist {
  id: string;
  kategorie: string;
  titel: string;
  /** ISO YYYY-MM-DD. */
  faelligAm: string;
  intervallMonate: number | null;
  zustaendig: string | null;
  notiz: string | null;
}

/** Dokument aus `project_documents` — OHNE storage_pfad, nur die id fuer den Endpunkt. */
export interface PortalDokument {
  id: string;
  titel: string;
  dateiname: string;
  kategorie: string;
  ordner: string;
  groesseBytes: number;
  mimeTyp: string | null;
  stand: string | null;
  gueltigBis: string | null;
  createdAt: string;
}

/** Alles, was die Kundenseite anzeigt — ein Aufruf von `sifa_kundenportal_laden`. */
export interface KundenportalDaten {
  geladenAm: string;
  /** Betreuendes Buero (Briefkopf), z. B. „GefAS mbH". */
  bueroName: string;
  betrieb: PortalBetrieb;
  sifaKontakt: PortalKontakt;
  mitarbeiter: PortalMitarbeiter[];
  unterweisungen: PortalUnterweisung[];
  teilnahmen: PortalTeilnahme[];
  fristen: PortalFrist[];
  dokumente: PortalDokument[];
}

/**
 * Warum eine Anfrage nicht durchkam. Die Werte 'unbekannt' | 'inaktiv' |
 * 'pin' | 'gesperrt' | 'eingabe' | 'keine_datei' kommen WOERTLICH aus den
 * RPCs (Nr. 94); 'schema' (Migration offen) und 'netz' (Endpunkt nicht
 * erreichbar) ergaenzt der Client.
 */
export type KundenportalFehlerArt =
  | 'unbekannt'
  | 'inaktiv'
  | 'pin'
  | 'gesperrt'
  | 'eingabe'
  | 'keine_datei'
  | 'schema'
  | 'netz';

export interface KundenportalStatus {
  ok: false;
  fehler: KundenportalFehlerArt;
  /** Bei 'pin': wie viele Versuche bis zur Sperre bleiben. */
  versucheRest?: number | null;
  /** Bei 'gesperrt': ISO-Zeitstempel, bis wann. */
  gesperrtBis?: string | null;
  /** Klartext (bei 'eingabe' aus der RPC, sonst vom Client). */
  text?: string | null;
}

/** Was der Kunde an einer Person aendern darf — genau diese Felder liest die RPC. */
export interface PortalMitarbeiterEingabe {
  id?: string | null;
  vorname: string;
  nachname: string;
  geburtsdatum?: string | null;
  email?: string | null;
  abteilung?: string | null;
  personalnummer?: string | null;
  aktiv?: boolean;
}

export type PortalDateiArt = 'dokument' | 'zertifikat';
